import { dpow } from "./dmath";
import { C, eras, perks } from "@bd/content";
import { BALANCE } from "./balance";
import { yearOf } from "./calendar";
import type { Company } from "./state";

let cache = new WeakMap<string[], { n: number; dir?: string; fx: Record<string, number> }>();
/** Forget the summed perks (every week, and every online quarter: see resetCaches). */
export function resetPerkCache(): void {
  cache = new WeakMap();
}

/** Summed effect of every researched perk for one key (e.g. "designSpeed", "credit"). */
export function perkFx(co: Company, key: string): number {
  let c = cache.get(co.researched);
  if (!c || c.n !== co.researched.length || c.dir !== co.direction) {
    const fx: Record<string, number> = {};
    for (const id of co.researched) for (const [k, v] of Object.entries(C.perk[id]?.fx ?? {})) fx[k] = (fx[k] ?? 0) + v;
    // the company direction chosen in year three adds its bonus the same way
    for (const [k, v] of Object.entries(co.direction ? C.direction[co.direction]?.fx ?? {} : {})) fx[k] = (fx[k] ?? 0) + v;
    c = { n: co.researched.length, dir: co.direction, fx };
    cache.set(co.researched, c);
  }
  return c.fx[key] ?? 0;
}

export function eraStart(eraId: string): number {
  return eras.find((e) => e.id === eraId)?.from ?? 1965;
}

/** Perks the company could research now: its era's row, or the next row at triple cost. */
/** Where a perk stands for you: yours, being researched, ready to research, or why not yet. */
export function perkState(co: Company, id: string, week: number): { state: "done" | "researching" | "open" | "locked" | "ruled_out"; why?: string } {
  const p = perks.find((x) => x.id === id);
  if (!p) return { state: "locked", why: "Unknown" };
  if (co.researched.includes(p.id)) return { state: "done" };
  if ((co.researching ?? []).some((r) => r.id === `pk:${p.id}`)) return { state: "researching" };
  const taken = [...co.researched, ...(co.researching ?? []).map((r) => r.id.slice(3))];
  const rival = (p.excludes ?? []).find((x) => taken.includes(x));
  if (rival) return { state: "ruled_out", why: `You chose ${perks.find((x) => x.id === rival)?.name ?? rival}` };
  const need = (p.requires ?? []).filter((x) => !co.researched.includes(x));
  if (need.length) return { state: "locked", why: `Needs ${need.map((x) => perks.find((q) => q.id === x)?.name ?? x).join(" and ")}` };
  if (yearOf(week) < eraStart(p.era) - 5) return { state: "locked", why: `From ${eraStart(p.era)}` };
  return { state: "open" };
}

export function perkOptions(co: Company, week: number): { id: string; name: string; cost: number; ahead: boolean; branch: string }[] {
  const year = yearOf(week);
  const out = [];
  for (const p of perks) {
    if (perkState(co, p.id, week).state !== "open") continue;
    const from = eraStart(p.era);
    const base = p.cost * eraCostMult(from);
    if (year >= from) out.push({ id: `pk:${p.id}`, name: p.name, cost: Math.round(base), ahead: false, branch: p.branch });
    else out.push({ id: `pk:${p.id}`, name: p.name, cost: Math.round(base * 3), ahead: true, branch: p.branch });
  }
  return out;
}

/** Research gets dearer each era. */
export function eraCostMult(year: number): number {
  const idx = Math.max(0, eras.filter((e) => e.from <= year).length - 1);
  return dpow(BALANCE.eraCost, idx);
}
